/**
 * Screenshot Capture Utility
 * Uses html-to-image for reliable DOM-to-image conversion
 * 
 * Handles external resources (images, fonts, stylesheets) via server-side proxy:
 * - Downloads images and converts to data URLs
 * - Fetches stylesheets, extracts font URLs, and injects modified CSS
 * - Converts fonts to data URLs to avoid CORS issues
 * All external resources are proxied through the backend to bypass CORS restrictions
 */

/**
 * Get API base URL from environment
 */
function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
}

/**
 * Download external resource via proxy and convert to data URL
 * This avoids CORS by routing through our server
 * Uses data URL instead of blob URL to avoid cross-origin iframe issues
 */
async function downloadResourceViaProxy(url: string): Promise<string> {
  try {
    const apiUrl = getApiBaseUrl();
    const proxyUrl = `${apiUrl}/api/editor/proxy-resource?url=${encodeURIComponent(url)}`;
    
    const response = await fetch(proxyUrl);
    if (!response.ok) {
      throw new Error(`Proxy fetch failed: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type') || '';
    
    // If it's CSS text, return as text instead of data URL
    if (contentType.includes('text/css') || url.includes('.css')) {
      return await response.text();
    }
    
    // For binary resources (images, fonts), convert to data URL
    const blob = await response.blob();
    
    // Convert blob to data URL (works across iframe origins)
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn(`Failed to download resource via proxy: ${url}`, error);
    throw error;
  }
}

/**
 * Check if URL is external (GCS, etc.)
 */
function isExternalResource(url: string): boolean {
  if (!url) return false;
  
  return (
    url.includes('storage.googleapis.com') ||
    url.includes('firebasestorage.app')
  );
}

/**
 * Extract all URLs from CSS text (for fonts, background images, etc.)
 */
function extractUrlsFromCSS(cssText: string): string[] {
  const urls: string[] = [];
  const urlRegex = /url\(['"]?([^'")]+)['"]?\)/gi;
  let match;
  
  while ((match = urlRegex.exec(cssText)) !== null) {
    const url = match[1];
    // Only include HTTP(S) URLs
    if (url.startsWith('http://') || url.startsWith('https://')) {
      urls.push(url);
    }
  }
  
  return urls;
}

/**
 * Replace URLs in CSS text with data URLs
 */
function replaceUrlsInCSS(cssText: string, urlMap: Map<string, string>): string {
  let result = cssText;
  
  urlMap.forEach((dataUrl, originalUrl) => {
    // Escape special regex characters in the URL
    const escapedUrl = originalUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Replace all occurrences of url(originalUrl), url('originalUrl'), url("originalUrl")
    const patterns = [
      new RegExp(`url\\(['"]?${escapedUrl}['"]?\\)`, 'gi')
    ];
    
    patterns.forEach(pattern => {
      result = result.replace(pattern, `url('${dataUrl}')`);
    });
  });
  
  return result;
}

/**
 * Capture one screenshot per game section (identified by [section-name] attribute),
 * making each section active in turn before the DOM clone happens.
 * The live iframe never visually changes — html-to-image clones the DOM at call time.
 * Returns one Blob per section found (skips sections with no visible content).
 */
export async function captureIframeAllSections(
  iframeRef: React.RefObject<HTMLIFrameElement>
): Promise<Blob[]> {
  if (!iframeRef.current) throw new Error('Iframe reference is not available');

  const iframe = iframeRef.current;
  const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDocument || !iframeDocument.body) throw new Error('Unable to access iframe document');

  const width = iframe.offsetWidth || 533;
  const height = iframe.offsetHeight || 680;

  const externalImages: Array<{ img: HTMLImageElement; originalSrc: string }> = [];
  const injectedStyles: HTMLStyleElement[] = [];

  // Track original active states so we can restore exactly
  const sections = Array.from(iframeDocument.querySelectorAll('section[section-name]')) as HTMLElement[];
  const originalActives = sections.map(s => s.classList.contains('active'));

  try {
    // Freeze animations once for the entire session
    const freezeStyle = iframeDocument.createElement('style');
    freezeStyle.setAttribute('data-screenshot-freeze', 'true');
    freezeStyle.textContent = `*, *::before, *::after { animation: none !important; transition: none !important; }`;
    iframeDocument.head.appendChild(freezeStyle);
    injectedStyles.push(freezeStyle);

    // Load external resources once (shared across all section captures)
    const downloadPromises: Promise<void>[] = [];

    iframeDocument.querySelectorAll<HTMLImageElement>('img').forEach(img => {
      if (img.src && isExternalResource(img.src)) {
        const originalSrc = img.src;
        downloadPromises.push(
          downloadResourceViaProxy(img.src)
            .then(dataUrl => { externalImages.push({ img, originalSrc }); img.src = dataUrl; })
            .catch(() => {})
        );
      }
    });

    for (const link of Array.from(iframeDocument.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'))) {
      if (!link.href || (!link.href.startsWith('http://') && !link.href.startsWith('https://'))) continue;
      downloadPromises.push((async () => {
        try {
          const cssText = await downloadResourceViaProxy(link.href);
          const fontUrls = extractUrlsFromCSS(cssText);
          const urlMap = new Map<string, string>();
          await Promise.all(fontUrls.map(async u => {
            try { urlMap.set(u, await downloadResourceViaProxy(u)); } catch {}
          }));
          const styleEl = iframeDocument.createElement('style');
          styleEl.textContent = fontUrls.length ? replaceUrlsInCSS(cssText, urlMap) : cssText;
          styleEl.setAttribute('data-injected-proxy', 'true');
          iframeDocument.head.appendChild(styleEl);
          injectedStyles.push(styleEl);
        } catch {}
      })());
    }

    for (const style of Array.from(iframeDocument.querySelectorAll<HTMLStyleElement>('style'))) {
      if (!style.textContent) continue;
      const externalFontUrls = extractUrlsFromCSS(style.textContent).filter(u => u.startsWith('http://') || u.startsWith('https://'));
      if (!externalFontUrls.length) continue;
      downloadPromises.push((async () => {
        try {
          const urlMap = new Map<string, string>();
          await Promise.all(externalFontUrls.map(async u => {
            try { urlMap.set(u, await downloadResourceViaProxy(u)); } catch {}
          }));
          const newStyle = iframeDocument.createElement('style');
          newStyle.textContent = replaceUrlsInCSS(style.textContent || '', urlMap);
          newStyle.setAttribute('data-injected-proxy', 'true');
          style.parentNode?.insertBefore(newStyle, style.nextSibling);
          injectedStyles.push(newStyle);
        } catch {}
      })());
    }

    await Promise.all(downloadPromises);

    // Settle after freeze + resource injection
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    await new Promise(resolve => setTimeout(resolve, 300));

    const { toBlob } = await import('html-to-image');
    const blobs: Blob[] = [];

    // If no named sections found, fall back to a single capture of whatever is visible
    const targets = sections.length > 0 ? sections : [null];

    for (const section of targets) {
      if (section) {
        // Make only this section active
        sections.forEach(s => s.classList.remove('active'));
        section.classList.add('active');
        // One rAF so the DOM reflects the class change before toBlob clones it
        await new Promise(resolve => requestAnimationFrame(resolve));
      }

      const blob = await toBlob(iframeDocument.documentElement, {
        width, height, pixelRatio: 2, cacheBust: true, skipFonts: true,
        style: { margin: '0', padding: '0' },
        filter: node => node.nodeName !== 'SCRIPT'
      });

      if (blob) blobs.push(blob);
    }

    // Restore original active states
    sections.forEach((s, i) => s.classList.toggle('active', originalActives[i]));

    return blobs;
  } finally {
    externalImages.forEach(({ img, originalSrc }) => { img.src = originalSrc; });
    injectedStyles.forEach(s => s.remove());
    // Ensure active states are always restored even on error
    sections.forEach((s, i) => s.classList.toggle('active', originalActives[i]));
  }
}

export async function captureIframeScreenshot(
  iframeRef: React.RefObject<HTMLIFrameElement>
): Promise<Blob> {
  if (!iframeRef.current) {
    throw new Error('Iframe reference is not available');
  }

  const iframe = iframeRef.current;
  const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

  if (!iframeDocument || !iframeDocument.body) {
    throw new Error('Unable to access iframe document');
  }

  const width = iframe.offsetWidth || 533;
  const height = iframe.offsetHeight || 680;

  // Track external images to restore after capture
  const externalImages: Array<{ img: HTMLImageElement; originalSrc: string; dataUrl: string }> = [];
  // Track injected styles to remove after capture
  const injectedStyles: HTMLStyleElement[] = [];

  try {
    // Freeze all animations and transitions so html-to-image's DOM clone
    // captures the current fully-visible state rather than an initial keyframe
    const freezeStyle = iframeDocument.createElement('style');
    freezeStyle.setAttribute('data-screenshot-freeze', 'true');
    freezeStyle.textContent = `
      *, *::before, *::after {
        animation: none !important;
        transition: none !important;
      }
    `;
    iframeDocument.head.appendChild(freezeStyle);
    injectedStyles.push(freezeStyle);

    // Flush the current paint and allow the freeze style to settle
    await new Promise(resolve => requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    }));
    await new Promise(resolve => setTimeout(resolve, 300));

    const downloadPromises: Promise<void>[] = [];

    // 1. Handle external images - download via proxy and convert to data URLs
    const images = iframeDocument.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      if (img.src && isExternalResource(img.src)) {
        const downloadPromise = downloadResourceViaProxy(img.src)
          .then(dataUrl => {
            const imageData = {
              img: img,
              originalSrc: img.src,
              dataUrl: dataUrl
            };
            externalImages.push(imageData);
            img.src = dataUrl; // Replace with data URL
          })
          .catch(error => {
            console.warn(`Failed to proxy image: ${img.src}`, error);
            // Keep original src if proxy fails
          });
        
        downloadPromises.push(downloadPromise);
      }
    });

    // 2. Handle external stylesheets and fonts
    const stylesheets = iframeDocument.querySelectorAll('link[rel="stylesheet"]');
    
    for (const linkElement of Array.from(stylesheets)) {
      const link = linkElement as HTMLLinkElement;
      if (!link.href) continue;
      
      // Check if it's an external stylesheet (including Google Fonts, etc.)
      if (link.href.startsWith('http://') || link.href.startsWith('https://')) {
        const stylesheetPromise = (async () => {
          try {
            // Fetch stylesheet via proxy
            const cssText = await downloadResourceViaProxy(link.href);
            
            // Extract font URLs from CSS
            const fontUrls = extractUrlsFromCSS(cssText);
            
            if (fontUrls.length === 0) {
              // No fonts, just inject the CSS
              const styleElement = iframeDocument.createElement('style');
              styleElement.textContent = cssText;
              styleElement.setAttribute('data-injected-proxy', 'true');
              iframeDocument.head.appendChild(styleElement);
              injectedStyles.push(styleElement);
              return;
            }
            
            // Download all fonts and create URL map
            const urlMap = new Map<string, string>();
            await Promise.all(
              fontUrls.map(async (fontUrl) => {
                try {
                  const fontDataUrl = await downloadResourceViaProxy(fontUrl);
                  urlMap.set(fontUrl, fontDataUrl);
                } catch (error) {
                  console.warn(`Failed to proxy font: ${fontUrl}`, error);
                }
              })
            );
            
            // Replace font URLs in CSS with data URLs
            const modifiedCSS = replaceUrlsInCSS(cssText, urlMap);
            
            // Inject modified CSS
            const styleElement = iframeDocument.createElement('style');
            styleElement.textContent = modifiedCSS;
            styleElement.setAttribute('data-injected-proxy', 'true');
            iframeDocument.head.appendChild(styleElement);
            injectedStyles.push(styleElement);
            
          } catch (error) {
            console.warn(`Failed to process stylesheet: ${link.href}`, error);
          }
        })();
        
        downloadPromises.push(stylesheetPromise);
      }
    }

    // 3. Handle inline styles with external font URLs
    const inlineStyles = iframeDocument.querySelectorAll('style');
    for (const styleElement of Array.from(inlineStyles)) {
      const style = styleElement as HTMLStyleElement;
      if (!style.textContent) continue;
      
      const fontUrls = extractUrlsFromCSS(style.textContent);
      const externalFontUrls = fontUrls.filter(url => url.startsWith('http://') || url.startsWith('https://'));
      
      if (externalFontUrls.length > 0) {
        const inlineStylePromise = (async () => {
          try {
            const urlMap = new Map<string, string>();
            await Promise.all(
              externalFontUrls.map(async (fontUrl) => {
                try {
                  const fontDataUrl = await downloadResourceViaProxy(fontUrl);
                  urlMap.set(fontUrl, fontDataUrl);
                } catch (error) {
                  console.warn(`Failed to proxy inline font: ${fontUrl}`, error);
                }
              })
            );
            
            // Replace font URLs with data URLs
            const modifiedCSS = replaceUrlsInCSS(style.textContent || '', urlMap);
            
            // Create new style element with modified CSS
            const newStyleElement = iframeDocument.createElement('style');
            newStyleElement.textContent = modifiedCSS;
            newStyleElement.setAttribute('data-injected-proxy', 'true');
            style.parentNode?.insertBefore(newStyleElement, style.nextSibling);
            injectedStyles.push(newStyleElement);
            
          } catch (error) {
            console.warn(`Failed to process inline style`, error);
          }
        })();
        
        downloadPromises.push(inlineStylePromise);
      }
    }

    // Wait for all resources to be downloaded via proxy
    await Promise.all(downloadPromises);

    // Dynamically import html-to-image
    const { toBlob } = await import('html-to-image');

    // Capture with skipFonts: true
    // We've already injected fonts as <style> tags with data URLs above,
    // so they'll be captured as part of the DOM. We skip html-to-image's
    // font processing to avoid errors with malformed font data.
    const blob = await toBlob(iframeDocument.documentElement, {
      width: width,
      height: height,
      pixelRatio: 2, // 2x resolution for higher quality images
      cacheBust: true,
      skipFonts: true, // Skip html-to-image's font processing - we've already injected fonts
      includeQueryParams: true,
      style: {
        margin: '0',
        padding: '0'
      },
      // Filter to include all visible elements
      filter: (node) => {
        // Skip script tags but include our injected styles
        if (node.nodeName === 'SCRIPT') {
          return false;
        }
        return true;
      }
    });

    if (!blob) {
      throw new Error('Failed to generate blob from screenshot');
    }

    // Restore original URLs (data URLs don't need cleanup)
    externalImages.forEach(({ img, originalSrc }) => {
      img.src = originalSrc;
    });

    // Remove injected style elements
    injectedStyles.forEach(style => {
      style.remove();
    });

    return blob;
  } catch (error) {
    console.error('Screenshot capture failed:', error);
    
    // Make sure to restore original URLs even if capture fails
    externalImages.forEach(({ img, originalSrc }) => {
      img.src = originalSrc;
    });

    // Clean up injected styles
    injectedStyles.forEach(style => {
      style.remove();
    });
    
    throw new Error(`Failed to capture screenshot: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

